import { html, css, LitElement } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map.js';
import { TableStyles } from '../styles/table-styles';

import '@trumedianetworks/tmn-core-components/src/tmn-icon';
import '@trumedianetworks/tmn-core-components/src/tmn-button';
import '@trumedianetworks/tmn-core-components/src/tmn-checkbox';
import '@trumedianetworks/tmn-core-components/src/tmn-spinner';

import { SORT_OPTIONS } from './table-constants';
import { FilterManager } from './filter-manager';
const { ASC_SORT, DESC_SORT } = SORT_OPTIONS;

// todo rename to tmn-table-basic maybe so as to not clash with ferp tmn-table-simple
// rework slightly for small screens (only show one sm col or different view on phone)
/**
 * Simple table
 * Takes data and shows in a table format
 *
 */

export class TmnTableSimple extends LitElement {
    constructor() {
        super();

        // this.tableData = {};
        // this.tableRows = [];
        this.orderBy = {
            columnKey: null,
            sortOrder: null,
        };
        this.sortable = true;
        this.dataRows = [];
        this.columnDefs = [];
        this.autoLimitRowCount = 250;
        this._isAutoLimitOn = true;
    }

    static get styles() {
        return [
            css`
                :host {
                    --tmn-table-td-padding: 6px;
                    --tmn-icon-width: 22px;
                }
            `,
            TableStyles.styles,
            css`
                :host {
                    display: block;
                    height: 100%;
                }
                :host([hidden]) {
                    display: none;
                }
                th {
                    font-size: 14px;
                    user-select: none;
                }
                .th-sort:hover {
                    background-color: #d6ecf4;
                    cursor: pointer;
                }
                td {
                    vertical-align: middle;
                    white-space: normal;
                }
                div.action-icon {
                    display: inline-block;
                    margin-right: 8px;
                }
                .sort-icon {
                    vertical-align: middle;
                }
                .action-icon > * {
                    display: inline-flex;
                }
            `,
        ];
    }

    static get properties() {
        return {
            dataRows: {
                type: Array,
            },
            columnDefs: {
                type: Array,
            },
            rowIdentifier: {
                type: String,
            },
            sortable: {
                type: Boolean,
            },
            orderBy: {
                type: Object,
            },
            selectedRowIds: {
                type: Array,
            },
            filterDefs: {
                type: Array,
            },
            autoLimitRowCount: {
                type: Number,
            },
            _isAutoLimitOn: {
                type: Boolean,
            },
            // /* private but used to trigger renders */
            _loadAllInPrograss: {
                type: Boolean,
            },
            // filterDefsSelectedData: {
            //     type: Object,
            // },
        };
    }

    firstUpdated() {
        super.firstUpdated();
        if (!this.autoLimitRowCount) {
            this._isAutoLimitOn = false;
        }
    }

    // separate render and render table to make overriding easier
    render() {
        return this.renderTable();
    }

    renderTable() {
        return html`
            <table>
                <thead>
                    <tr>
                        ${this.renderTableDataheader()}
                    </tr>
                </thead>
                <tbody>
                    ${this.renderTableDataRows()} ${this.renderIfNotHasResults()}
                </tbody>
            </table>
            ${this.renderTableFooter()}
        `;
    }

    getFilteredColumnDefs() {
        const columnDefs = this.getColumnDefs();
        return columnDefs.filter((h) => !h.hidden);
    }

    renderTableDataheader() {
        const classes = {
            'th-sort': this.sortable,
        };
        const columnDefs = this.getFilteredColumnDefs();

        return columnDefs.map((column) => {
            const headerFormatter = column?.headerFormatter;
            return html`<th
                class=${classMap(classes)}
                .value=${column}
                @click=${this.handleHeaderColumnClick}>
                ${headerFormatter ? headerFormatter(column) : html`<span>${column.label}</span>`}
                ${this.renderIfSortable(column)}
            </th> `;
        });
    }

    renderTableDataRows() {
        const rows = this.getLimitedRows();
        const columDefs = this.getColumnDefs();
        return rows.map(
            (row, index) => html`
                <tr
                    @click=${this.onRowClick}
                    data-tmn-data-row-id=${row.metaData?.id}
                    class=${classMap(this.classesForRow(row))}
                    style=${this.styleForRow(row)}>
                    ${this.renderDataRow(row, columDefs)}
                </tr>
            `
        );
    }

    renderDataRow(row, columDefs) {
        return row.data
            .map((cellValue, idx) => {
                if (columDefs[idx]?.hidden) {
                    return false;
                }

                const formatter = columDefs[idx]?.dataFormatter;
                if (formatter) {
                    return html`<td>${formatter(cellValue, row)}</td>`;
                }
                return html`<td>
                    ${Array.isArray(cellValue) ? cellValue.join(', ') : cellValue}
                </td> `;
            })
            .filter((val) => val);
    }

    renderTableFooter() {
        const { autoLimitRowCount, _loadAllInPrograss, _isAutoLimitOn } = this;
        // consider whether to memoize this or otherwise try to avoid the multiple filter calls
        const rows = this.getRows();
        const totalRows = rows?.length;
        const showLimitToggleButton =
            (autoLimitRowCount && totalRows > autoLimitRowCount) || _loadAllInPrograss;
        if (showLimitToggleButton) {
            const limitToggleText = _isAutoLimitOn
                ? 'Show max rows'
                : `Limit to ${autoLimitRowCount} rows`;

            const icon = _isAutoLimitOn ? 'expand_more' : 'expand_less';

            return html`
                <tmn-button
                    class="footerShowMoreButton"
                    variant="text"
                    .justifyContent=${'flex-start'}
                    @click=${this.handleToggleRowLimit}
                    ?disabled=${_loadAllInPrograss}>
                    ${_loadAllInPrograss
                        ? html`
                              <tmn-spinner
                                  slot="startIcon"
                                  size="12px"
                                  variant="simple"></tmn-spinner>
                          `
                        : html` <tmn-icon slot="startIcon" icon="${icon}"></tmn-icon> `}
                    <span class="results-text">${limitToggleText}</span>
                </tmn-button>
            `;
        }
    }
    async handleToggleRowLimit(e) {
        this._loadAllInPrograss = true;
        // do this little dance to allow the spinner to start
        setTimeout(() => {
            this._isAutoLimitOn = !this._isAutoLimitOn;
            this.updateComplete.then(() => {
                this._loadAllInPrograss = false;
            });
        }, 5);
    }

    renderIfNotHasResults() {
        if (!this.hasResults()) {
            return html`
                <tr>
                    <td colspan="${this.colCount()}">No results found</td>
                </tr>
            `;
        }
    }

    renderIfSortable(column) {
        if (this.sortable && this.isSortedOn(column.key)) {
            const { orderBy } = this;
            const sortOrder = orderBy?.sortOrder?.toUpperCase();
            const sortIconClass = sortOrder === ASC_SORT ? 'arrow_drop_up' : 'arrow_drop_down';
            return html` <tmn-icon class="sort-icon" icon=${sortIconClass}></tmn-icon> `;
        }
    }

    // allow for simple overrides in derived classes
    getRows() {
        const { rowIdentifier, filterDefs } = this;
        const augmentedColumnDefs = this.getColumnDefs();
        const dataKeys = augmentedColumnDefs.map((d) => d.key);
        if (!rowIdentifier) {
            console.warn('No row identifier specified');
        }
        const sortedRows = this.sortTableRows();
        // todo maybe we don't have to do this now and can just get data and meta data on demand by rowId?
        // for pass1 we'll just do this like we used to
        const filteredRows = filterDefs?.length ? this.filterRows(sortedRows) : sortedRows;
        const augmentedRows = filteredRows.map((row) => {
            const data = dataKeys.map((key) => row[key]);
            const id = row[rowIdentifier];
            const metaData = {
                id,
                rawData: row,
            };

            return {
                data,
                metaData,
            };
        });
        return augmentedRows;
    }

    // uses rowCount/autolimit system, this function can be used for functions like select all when
    // we only want to operate on the visible rows
    getLimitedRows() {
        const { autoLimitRowCount, _isAutoLimitOn } = this;
        const rows = this.getRows();
        const totalRows = rows?.length;
        if (!totalRows) {
            return [];
        }
        if (autoLimitRowCount && _isAutoLimitOn) {
            return rows.slice(0, autoLimitRowCount);
        }

        return rows;
    }

    filterRows(rows = []) {
        const { filterDefs } = this;
        return rows.filter((row) => FilterManager.evaluateRow(row, filterDefs));
    }

    getColumnDefs() {
        const { columnDefs = [] } = this;
        return columnDefs.filter((d) => d.key);
    }

    styleForRow(row) {
        return row.metaData.style || '';
    }

    classesForRow(row) {
        const classes = {};
        if (row.metaData.class) {
            classes[row.metaData.class] = row.metaData.class;
        }
        return classes;
    }

    handleHeaderColumnClick(e) {
        if (this.sortable) {
            const column = e.currentTarget.value;
            this.orderBy.columnKey = column.key;
            this.toggleSortOrder();
            //this.sortTableRows();
        }
    }

    sortTableRows() {
        const { orderBy, dataRows, sortable } = this;
        if (!(sortable && orderBy && dataRows?.length)) {
            return dataRows;
        }
        const _columnDefs = this.getColumnDefs();
        const { columnKey, sortOrder } = orderBy;
        // note since we get the colDef we could even include a customSorter
        const sortCol = _columnDefs?.find((def) => def.key === columnKey);
        if (!sortCol) {
            return dataRows;
        }
        const sortType = sortCol?.type;
        const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });
        const sortedRows = dataRows.slice().sort((a, b) => {
            const aVal = a[sortCol.key];
            const bVal = b[sortCol.key];
            if (sortType == 'date') {
                const aDate = new Date(aVal);
                const bDate = new Date(bVal);
                return bDate - aDate;
            }
            return collator.compare(aVal, bVal);
        });

        if (sortOrder === DESC_SORT) {
            sortedRows.reverse();
        }

        //this.dataRows = sortedRows;
        return sortedRows;
    }

    toggleSortOrder() {
        const { orderBy = {} } = this;
        const newOrderBy = {
            ...orderBy,
            sortOrder: orderBy.sortOrder === ASC_SORT ? DESC_SORT : ASC_SORT,
        };
        this.orderBy = newOrderBy;
        //this.orderBy.sortOrder = ;
    }

    isSortedOn(columnKey) {
        if (this.orderBy == null) {
            return false;
        } else {
            return columnKey === this.orderBy.columnKey;
        }
    }

    rowDataForAction(e) {
        const trEl = e.target.closest('tr');
        const rowId = trEl.dataset.tmnDataRowId;
        const { dataRows, rowIdentifier } = this;
        const rowDetails = dataRows.find((r) => r[rowIdentifier]?.toString() === rowId);
        if (rowDetails) {
            return {
                ...rowDetails,
            };
        }
    }

    hasResults() {
        const rows = this.getRows();
        return rows?.length;
    }

    colCount() {
        const colDefs = this.getColumnDefs();
        return colDefs?.filter((h) => !h.hidden)?.length;
    }

    onRowClick(e) {
        const rowData = this.rowDataForAction(e);
        const id = rowData[this.rowIdentifier];
        this.handleRowClick(e, id, rowData);
    }

    handleRowClick(e, id, rowData) {}
}

window.customElements.define('tmn-table-simple', TmnTableSimple);
